import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Shield, ArrowLeft, Wrench, AlertTriangle, Building2, ArrowRight, Loader2, Gift, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { APP_VERSION } from "../appVersion";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
    <rect x="12" y="1" width="10" height="10" fill="#7FBA00"/>
    <rect x="1" y="12" width="10" height="10" fill="#00A4EF"/>
    <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
  </svg>
);

const LoginPage = () => {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const [loadingBtn, setLoadingBtn] = useState(null);
  const [notice, setNotice] = useState(null);
  const [providers, setProviders] = useState(null);
  const [showCompanySso, setShowCompanySso] = useState(false);
  const [companyEmail, setCompanyEmail] = useState("");
  const [ssoLookupState, setSsoLookupState] = useState(null); // null | "loading" | "not_found" | {found, company_name, sso_id}
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [pwEmail, setPwEmail] = useState("");
  const [pwPassword, setPwPassword] = useState("");
  const [pwError, setPwError] = useState(null);

  // PR_RUN_052 — Promo-code redemption flow.
  // 3-state UI: idle (button) · expanded (input + validate) · validated (locked-in chip).
  // Auto-pre-fills from ?promo=... URL param (printed-flyer QR carries this).
  // Validated codes are stored in localStorage under `pending_promo_code` so
  // they survive the OAuth round-trip; App.js redeems them on the first
  // authenticated `/auth/me` response.
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoState, setPromoState] = useState(null); // null | "checking" | {valid, trial_days, expires_at_utc, remaining_redemptions, campaign_tag} | {valid: false, reason}
  const [promoApplied, setPromoApplied] = useState(false);

  // PR_RUN_052 — Validate the entered promo code against the public /check
  // endpoint. Debounced effectively by manual user action (no auto-validate
  // on every keystroke; user clicks "Validate" or submits). Stores the
  // validated code in localStorage so it survives the OAuth round-trip.
  const validatePromoCode = useCallback(async (raw) => {
    const code = (raw || "").trim().toUpperCase();
    if (!code) {
      setPromoState(null);
      return;
    }
    setPromoState("checking");
    try {
      const res = await fetch(`${API}/marketing/promo/check/${encodeURIComponent(code)}`);
      const data = await res.json();
      if (data?.valid) {
        setPromoState(data);
        setPromoApplied(true);
        // Persist for redemption immediately after the user authenticates.
        try { localStorage.setItem("pending_promo_code", code); } catch { /* private mode */ }
      } else {
        setPromoState({ valid: false, reason: data?.reason || "code_invalid" });
        setPromoApplied(false);
        try { localStorage.removeItem("pending_promo_code"); } catch { /* private mode */ }
      }
    } catch {
      setPromoState({ valid: false, reason: "network_error" });
    }
  }, []);

  // PR_RUN_052 — URL-param prefill (?promo=CHARSTAN-LAUNCH). The printed
  // flyer's QR code links here directly; the user shouldn't have to type
  // the code manually. We auto-expand the promo section and validate
  // synchronously so the success chip is visible before SSO click.
  useEffect(() => {
    const urlPromo = searchParams.get("promo");
    if (urlPromo) {
      const code = urlPromo.trim().toUpperCase();
      setPromoCode(code);
      setShowPromoInput(true);
      validatePromoCode(code);
    } else {
      // Restore any code that was validated earlier in this session (e.g.
      // user came from /login?promo=... then refreshed without the param).
      try {
        const stored = localStorage.getItem("pending_promo_code");
        if (stored) {
          setPromoCode(stored);
          setShowPromoInput(true);
          validatePromoCode(stored);
        }
      } catch { /* private mode */ }
    }
  }, [searchParams, validatePromoCode]);

  // PR_RUN_052 — Promo-code reason → user-facing copy.
  const promoReasonLabel = (reason) => {
    const map = {
      code_not_found: "Code not recognized. Check the spelling and try again.",
      code_revoked: "This code has been deactivated.",
      code_expired: "This code has expired.",
      code_exhausted: "This code has reached its redemption limit.",
      network_error: "Couldn't reach the validation service. Try again in a moment.",
      code_invalid: "This code is not valid.",
    };
    return map[reason] || "This code is not valid.";
  };

  const clearPromoCode = () => {
    setPromoCode("");
    setPromoState(null);
    setPromoApplied(false);
    setShowPromoInput(false);
    try { localStorage.removeItem("pending_promo_code"); } catch { /* private mode */ }
  };

  useEffect(() => {
    const error = searchParams.get("error");
    if (error) {
      const msgs = {
        no_code: "SSO login was cancelled or failed. Please try again.",
        token_failed: "SSO authentication failed. Please try again or use a different method.",
        callback_failed: "SSO callback failed. Please try again.",
      };
      setNotice(msgs[error] || "Login failed. Please try again.");
      setTimeout(() => setNotice(null), 5000);
    }
  }, [searchParams]);

  // AUTH-LOOP-01 defensive hardening: when a user lands on /login it means
  // they are NOT authenticated (or were just bounced by ProtectedRoute).
  // Any remaining `session_token` cookie is therefore stale — nuke it
  // server-side so the next /auth/me call doesn't keep returning a phantom
  // session that re-redirects us into a loop. Also strip any leftover
  // `#session_id=...` fragment from a half-completed OAuth callback.
  // Gated on document.cookie containing `session_token` so we don't burn
  // the per-IP rate limiter on legitimate /login -> back -> /login flows.
  useEffect(() => {
    const purgeStaleSession = async () => {
      try {
        if (typeof window !== 'undefined' && window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        const hasSessionCookie =
          typeof document !== 'undefined' &&
          typeof document.cookie === 'string' &&
          document.cookie.includes('session_token');
        if (hasSessionCookie) {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        }
      } catch {
        // best-effort; never block the login UI
      }
    };
    purgeStaleSession();
  }, []);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const res = await fetch(`${API}/auth/providers`);
        const data = await res.json();
        setProviders(data.providers);
      } catch {
        // Fallback: SSO buttons always available, dev_login gated by env
        setProviders({
          google: { available: true },
          microsoft: { available: true },
          apple: { available: true },
          enterprise_sso: { available: true },
          dev_login: { available: false },
        });
      }
    };
    fetchProviders();
  }, []);

  // Core SSO providers are always shown regardless of API state
  const isCoreSso = (key) => ["google", "microsoft", "apple"].includes(key);
  const isAvailable = (key) => isCoreSso(key) ? true : providers?.[key]?.available;

  const handleCompanySsoLookup = async (e) => {
    e.preventDefault();
    if (!companyEmail.includes("@")) {
      setSsoLookupState("invalid");
      return;
    }
    setSsoLookupState("loading");
    try {
      const res = await axios.post(`${API}/enterprise/sso/lookup`, { email: companyEmail });
      if (res.data.found) {
        setSsoLookupState(res.data);
        // Auto-redirect after brief confirmation
        setTimeout(() => {
          window.location.href = `${API}/enterprise/sso/login/${res.data.sso_id}`;
        }, 1200);
      } else {
        setSsoLookupState("not_found");
      }
    } catch {
      setSsoLookupState("not_found");
    }
  };

  const handleGoogleLogin = () => {
    setLoadingBtn("google");
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e?.preventDefault?.();
    setLoadingBtn("password");
    setPwError(null);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
      setUser(null);
      const res = await axios.post(
        `${API}/auth/password-login`,
        { email: pwEmail.trim().toLowerCase(), password: pwPassword },
        { withCredentials: true }
      );
      setUser(res.data);
      navigate(res.data.role === "client" ? "/client-portal" : "/dashboard", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 423) {
        setPwError("Account temporarily locked due to too many failed attempts. Please wait and try again.");
      } else if (status === 429) {
        setPwError("Too many login attempts. Please wait a minute and try again.");
      } else if (status === 401) {
        setPwError("Invalid email or password.");
      } else {
        setPwError(err?.response?.data?.detail || "Login failed. Please try again.");
      }
    } finally {
      setLoadingBtn(null);
    }
  };

  const handleRoleLogin = async (role) => {
    setLoadingBtn(role);
    try {
      // Clear any existing session before switching roles
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
      setUser(null);

      const roleMap = {
        admin: { email: "admin@charstan.com", name: "Admin User", role: "admin" },
        auditor: { email: "auditor@charstan.com", name: "Project Manager", role: "auditor" },
        client: { email: "client@charstan.com", name: "Client User", role: "client" },
      };
      const creds = roleMap[role];
      const res = await axios.post(`${API}/auth/dev-login`, creds, { withCredentials: true });
      setUser(res.data);
      navigate(role === "client" ? "/client-portal" : "/dashboard", { replace: true });
    } catch (err) {
      setNotice(`${role} login failed.`);
      setTimeout(() => setNotice(null), 3000);
    } finally {
      setLoadingBtn(null);
    }
  };

  // Production hostname check: hide dev login on production domains
  const isProductionHost = typeof window !== 'undefined' && 
    (window.location.hostname === 'charstan.com' || 
     window.location.hostname === 'app.charstan.com' ||
     window.location.hostname === 'www.charstan.com');
  const showDevLogin = isAvailable("dev_login") && !isProductionHost;

  return (
    <div className="login-container" data-testid="login-page">
      <div className="login-card">
        {/* Logo */}
        <div className="flex items-center justify-center mb-6">
          <img src="/logo.svg" alt="Charstan" className="h-14" style={{ filter: 'brightness(1.1)' }} />
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="font-['Syne'] text-xl font-bold text-white mb-1">
            QMS Platform Access
          </h1>
          <p className="text-[#8B95A5] text-sm">
            Sign in to your quality management dashboard
          </p>
        </div>

        {/* Notice */}
        {notice && (
          <div className="mb-4 p-3 bg-[rgba(0,200,224,0.1)] border border-[rgba(0,200,224,0.2)] rounded-[3px] text-[#38BDF8] text-sm text-center" data-testid="login-notice">
            {notice}
          </div>
        )}

        {/* Conference Demo Banner — preview hosts only (hidden on app.charstan.com) */}
        {!isProductionHost && (
          <div className="mb-4 p-3 rounded-[3px] border border-[rgba(0,200,224,0.25)] bg-[linear-gradient(135deg,rgba(0,200,224,0.08),rgba(29,113,184,0.06))]" data-testid="demo-credentials-banner">
            <div className="flex items-start gap-2">
              <div className="w-1 self-stretch bg-[#00C8E0] rounded-full" />
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-widest text-[#00C8E0] font-semibold mb-1">Conference Demo · Try the platform</p>
                <div className="text-[#E2E8F0] text-xs space-y-0.5 mb-2 font-mono">
                  <div><span className="text-[#8B95A5]">email   </span><span className="text-white">demo@charstan.com</span></div>
                  <div><span className="text-[#8B95A5]">password</span> <span className="text-white">Charstan-Demo-2026</span></div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordLogin(true);
                    setPwEmail("demo@charstan.com");
                    setPwPassword("Charstan-Demo-2026");
                    setPwError(null);
                  }}
                  className="text-[11px] text-[#00C8E0] hover:text-white transition-colors underline-offset-2 hover:underline"
                  data-testid="demo-credentials-fill-btn"
                >
                  Auto-fill and sign in →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PR_RUN_052 — Promo-code banner (printed-flyer reconciliation).
            Always rendered; shows different states based on URL-param presence
            + user interaction. The validated code is stored in localStorage
            and redeemed automatically by App.js after the user authenticates. */}
        <div className="mb-4" data-testid="promo-banner">
          {!showPromoInput && !promoApplied && (
            <button
              type="button"
              onClick={() => setShowPromoInput(true)}
              className="w-full p-2.5 rounded-[3px] border border-dashed border-[rgba(201,168,76,0.3)] bg-[rgba(201,168,76,0.04)] hover:bg-[rgba(201,168,76,0.08)] hover:border-[rgba(201,168,76,0.5)] transition-colors flex items-center justify-center gap-2 text-[12px] text-[#C9A84C]"
              data-testid="promo-banner-toggle"
            >
              <Gift size={14} />
              <span>Have a promo code? Click to enter it</span>
            </button>
          )}

          {showPromoInput && !promoApplied && (
            <div className="p-3 rounded-[3px] border border-[rgba(201,168,76,0.3)] bg-[rgba(201,168,76,0.05)]" data-testid="promo-input-form">
              <div className="flex items-center gap-2 mb-2">
                <Gift size={14} className="text-[#C9A84C]" />
                <span className="text-[11px] uppercase tracking-widest text-[#C9A84C] font-semibold">Promo code</span>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); if (promoState && promoState !== "checking") setPromoState(null); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); validatePromoCode(promoCode); } }}
                  placeholder="e.g. CHARSTAN-LAUNCH"
                  className="flex-1 bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-white text-sm placeholder-[#555] outline-none focus:border-[#C9A84C] transition-colors font-mono uppercase"
                  data-testid="promo-code-input"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  onClick={() => validatePromoCode(promoCode)}
                  disabled={!promoCode || promoState === "checking"}
                  className="px-3 py-2 rounded bg-[#C9A84C] text-[#0D0F14] text-sm font-medium hover:bg-[#D6B663] transition-colors disabled:opacity-40 flex items-center gap-1"
                  data-testid="promo-validate-btn"
                >
                  {promoState === "checking" ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
                </button>
              </div>
              {promoState && promoState !== "checking" && promoState.valid === false && (
                <p className="mt-2 text-red-400 text-[11px] flex items-center gap-1" data-testid="promo-invalid-msg">
                  <X size={12} /> {promoReasonLabel(promoState.reason)}
                </p>
              )}
              <button
                type="button"
                onClick={() => { setShowPromoInput(false); setPromoCode(""); setPromoState(null); }}
                className="mt-2 text-[#8B95A5] text-[11px] hover:text-white transition-colors"
                data-testid="promo-cancel-btn"
              >
                Cancel
              </button>
            </div>
          )}

          {promoApplied && promoState && promoState.valid && (
            <div className="p-3 rounded-[3px] border border-[rgba(52,211,153,0.3)] bg-[rgba(52,211,153,0.06)] flex items-start gap-2" data-testid="promo-applied-chip">
              <div className="mt-0.5 w-5 h-5 rounded-full bg-[#34D399] flex items-center justify-center flex-shrink-0">
                <Check size={12} className="text-[#0D0F14]" strokeWidth={3} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-widest text-[#34D399] font-semibold">Promo applied</span>
                  <span className="text-[11px] text-white font-mono">{promoCode}</span>
                </div>
                <p className="text-[11px] text-[#8B95A5] mt-0.5">
                  <strong className="text-white">{promoState.trial_days}-day</strong> free trial activates as soon as you sign in below.
                  {promoState.expires_at_utc ? ` Code valid until ${promoState.expires_at_utc.slice(0, 10)}.` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={clearPromoCode}
                className="text-[#8B95A5] hover:text-white transition-colors"
                aria-label="Remove promo code"
                data-testid="promo-applied-remove-btn"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>

        {/* SSO Buttons */}
        <div className="space-y-2.5">
          {isAvailable("google") && (
            <button
              onClick={handleGoogleLogin}
              disabled={!!loadingBtn}
              className="sso-button"
              data-testid="google-login-btn"
            >
              <GoogleIcon />
              <span>{loadingBtn === "google" ? "Connecting..." : "Continue with Google"}</span>
            </button>
          )}

          {isAvailable("microsoft") && (
            <button
              onClick={() => { setLoadingBtn("microsoft"); window.location.href = `${API}/auth/microsoft`; }}
              disabled={!!loadingBtn}
              className="sso-button"
              data-testid="microsoft-login-btn"
            >
              <MicrosoftIcon />
              <span>{loadingBtn === "microsoft" ? "Connecting..." : "Continue with Microsoft"}</span>
            </button>
          )}

          {isAvailable("apple") && (
            <button
              onClick={() => { setLoadingBtn("apple"); window.location.href = `${API}/auth/apple`; }}
              disabled={!!loadingBtn}
              className="sso-button"
              data-testid="apple-login-btn"
            >
              <AppleIcon />
              <span>{loadingBtn === "apple" ? "Connecting..." : "Continue with Apple"}</span>
            </button>
          )}

          {/* Company SSO — email domain lookup */}
          {!showCompanySso ? (
            <button
              onClick={() => setShowCompanySso(true)}
              className="sso-button"
              data-testid="company-sso-btn"
            >
              <Building2 size={18} />
              <span>Sign in with Company SSO</span>
            </button>
          ) : (
            <div className="p-4 rounded border border-[rgba(0,200,224,0.15)] bg-[rgba(0,200,224,0.04)]" data-testid="company-sso-form">
              <form onSubmit={handleCompanySsoLookup} className="space-y-3">
                <label className="text-[#8B95A5] text-xs block">Enter your company email</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={companyEmail}
                    onChange={(e) => { setCompanyEmail(e.target.value); setSsoLookupState(null); }}
                    placeholder="you@company.com"
                    autoFocus
                    className="flex-1 bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-white text-sm placeholder-[#555] outline-none focus:border-[#00C8E0] transition-colors"
                    data-testid="company-sso-email-input"
                  />
                  <button
                    type="submit"
                    disabled={ssoLookupState === "loading"}
                    className="px-3 py-2 rounded bg-[#00C8E0] text-[#0A0F1C] text-sm font-medium hover:bg-[#00B4CC] transition-colors disabled:opacity-50"
                    data-testid="company-sso-submit-btn"
                  >
                    {ssoLookupState === "loading" ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  </button>
                </div>

                {ssoLookupState === "invalid" && (
                  <p className="text-red-400 text-xs" data-testid="sso-error-invalid">Please enter a valid email address.</p>
                )}

                {ssoLookupState === "not_found" && (
                  <p className="text-[#E8A838] text-xs" data-testid="sso-error-not-found">
                    Your organization hasn't configured SSO yet. Contact your IT administrator or use another sign-in method.
                  </p>
                )}

                {ssoLookupState && ssoLookupState.found && (
                  <div className="flex items-center gap-2 text-[#34D399] text-xs" data-testid="sso-found">
                    <Shield size={14} />
                    <span>Redirecting to <strong>{ssoLookupState.company_name || ssoLookupState.domain}</strong> login...</span>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => { setShowCompanySso(false); setSsoLookupState(null); setCompanyEmail(""); }}
                  className="text-[#8B95A5] text-xs hover:text-white transition-colors"
                  data-testid="company-sso-back-btn"
                >
                  Use another sign-in method
                </button>
              </form>
            </div>
          )}

          {/* Email + Password — for demo/service accounts */}
          {!showPasswordLogin ? (
            <button
              onClick={() => setShowPasswordLogin(true)}
              className="sso-button"
              data-testid="password-login-toggle-btn"
            >
              <Shield size={18} />
              <span>Sign in with email & password</span>
            </button>
          ) : (
            <div className="p-4 rounded border border-[rgba(0,200,224,0.15)] bg-[rgba(0,200,224,0.04)]" data-testid="password-login-form">
              <form onSubmit={handlePasswordLogin} className="space-y-3">
                <div>
                  <label className="text-[#8B95A5] text-xs block mb-1">Email</label>
                  <input
                    type="email"
                    value={pwEmail}
                    onChange={(e) => { setPwEmail(e.target.value); setPwError(null); }}
                    placeholder="you@charstan.com"
                    autoFocus
                    autoComplete="username"
                    required
                    className="w-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-white text-sm placeholder-[#555] outline-none focus:border-[#00C8E0] transition-colors"
                    data-testid="password-login-email-input"
                  />
                </div>
                <div>
                  <label className="text-[#8B95A5] text-xs block mb-1">Password</label>
                  <input
                    type="password"
                    value={pwPassword}
                    onChange={(e) => { setPwPassword(e.target.value); setPwError(null); }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    className="w-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-white text-sm placeholder-[#555] outline-none focus:border-[#00C8E0] transition-colors"
                    data-testid="password-login-password-input"
                  />
                </div>

                {pwError && (
                  <p className="text-red-400 text-xs" data-testid="password-login-error">{pwError}</p>
                )}

                <button
                  type="submit"
                  disabled={loadingBtn === "password" || !pwEmail || !pwPassword}
                  className="w-full px-3 py-2 rounded bg-[#00C8E0] text-[#0A0F1C] text-sm font-medium hover:bg-[#00B4CC] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="password-login-submit-btn"
                >
                  {loadingBtn === "password" ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  <span>{loadingBtn === "password" ? "Signing in..." : "Sign in"}</span>
                </button>

                <button
                  type="button"
                  onClick={() => { setShowPasswordLogin(false); setPwError(null); setPwEmail(""); setPwPassword(""); }}
                  className="text-[#8B95A5] text-xs hover:text-white transition-colors"
                  data-testid="password-login-back-btn"
                >
                  Use another sign-in method
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Dev Login Section — only shown when DEV_LOGIN_ENABLED and not on production */}
        {showDevLogin && (
          <>
            {/* Divider */}
            <div className="flex items-center gap-4 my-5">
              <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
              <span className="text-[#8B95A5] text-[10px] uppercase tracking-widest">dev mode</span>
              <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
            </div>

            {/* Dev environment notice */}
            <div className="mb-3 p-2 bg-[rgba(232,168,56,0.08)] border border-[rgba(232,168,56,0.2)] rounded-[3px] flex items-center gap-2">
              <AlertTriangle size={14} className="text-[#E8A838] flex-shrink-0" />
              <p className="text-[#E8A838] text-[11px]">
                Development mode — test logins are enabled for this environment only.
              </p>
            </div>

            {/* Admin Test Login */}
            <button
              onClick={() => handleRoleLogin("admin")}
              disabled={!!loadingBtn}
              className="sso-button w-full border-dashed border-[#E8A838] text-[#E8A838] hover:bg-[rgba(232,168,56,0.1)]"
              data-testid="dev-login-btn"
            >
              <Wrench size={18} />
              <span>{loadingBtn === "admin" ? "Logging in..." : "Admin Test Login"}</span>
            </button>

            {/* Quick Access Role Buttons */}
            <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.06)]">
              <p className="text-[#8B95A5] text-[10px] uppercase tracking-wider text-center mb-3">Quick Access</p>
              <div className="flex justify-center gap-4">
                <button onClick={() => handleRoleLogin("admin")} disabled={!!loadingBtn}
                  className="text-center group cursor-pointer transition-all" data-testid="role-login-admin">
                  <span className="badge badge-cyan text-xs group-hover:ring-2 group-hover:ring-cyan-400/40 transition-all">
                    {loadingBtn === "admin" ? "..." : "Admin"}
                  </span>
                  <p className="text-[#8B95A5] text-xs mt-1 group-hover:text-white transition-colors">Full Access</p>
                </button>
                <button onClick={() => handleRoleLogin("auditor")} disabled={!!loadingBtn}
                  className="text-center group cursor-pointer transition-all" data-testid="role-login-auditor">
                  <span className="badge badge-blue text-xs group-hover:ring-2 group-hover:ring-blue-400/40 transition-all">
                    {loadingBtn === "auditor" ? "..." : "PM / Auditor"}
                  </span>
                  <p className="text-[#8B95A5] text-xs mt-1 group-hover:text-white transition-colors">Assigned Projects</p>
                </button>
                <button onClick={() => handleRoleLogin("client")} disabled={!!loadingBtn}
                  className="text-center group cursor-pointer transition-all" data-testid="role-login-client">
                  <span className="badge badge-green text-xs group-hover:ring-2 group-hover:ring-green-400/40 transition-all">
                    {loadingBtn === "client" ? "..." : "Client"}
                  </span>
                  <p className="text-[#8B95A5] text-xs mt-1 group-hover:text-white transition-colors">Own Dashboard</p>
                </button>
              </div>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-[#8B95A5] text-xs">
            By continuing, you agree to Charstan's{" "}
            <a href="/terms" className="text-[#38BDF8] hover:underline">Terms of Service</a> and{" "}
            <a href="/privacy" className="text-[#38BDF8] hover:underline">Privacy Policy</a>.
          </p>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 mx-auto text-[#38BDF8] hover:text-white text-sm mt-4 transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft size={14} />
            Back to Home
          </button>
          {/* PR_RUN_056 — Deployment / fork-session version stamp. Quiet
              footer badge so support / sales reps reading from a screenshot
              can call out exactly which version a user is on. */}
          <p
            className="mt-4 text-[10px] text-[#555] font-mono tracking-tight select-text"
            data-testid="login-app-version"
            title="App deployment / fork-session version stamp"
          >
            {APP_VERSION}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
